/**
 * Conversion Drill-Down Modal Component for Admin Control Panel
 * 
 * Phase 2: Displays detailed data for each funnel stage.
 */
import React, { useState, useEffect, useCallback } from "react";
import { 
  Search, ChevronLeft, ChevronRight, ArrowUpDown, 
  ArrowUp, ArrowDown, Loader2, X, Users, UserPlus,
  BookOpen, Play, CheckCircle, AlertTriangle
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { API_URL } from '@/config/api';

const stageConfig = {
  visitors: {
    icon: Users,
    iconColor: "text-slate-400",
    iconBg: "bg-slate-800",
    columns: [
      { key: "anonymous_id", label: "Visitor", sortable: true },
      { key: "pages_viewed", label: "Pages", sortable: true, align: "right" },
      { key: "location", label: "Location", sortable: false },
      { key: "first_visit", label: "First Visit", sortable: true },
      { key: "last_visit", label: "Last Visit", sortable: true },
      { key: "bounced", label: "Bounced?", sortable: false },
      { key: "converted", label: "Converted?", sortable: false }
    ],
    clickable: true
  },
  accounts: {
    icon: UserPlus,
    iconColor: "text-blue-400",
    iconBg: "bg-blue-900/50",
    columns: [
      { key: "email", label: "Email", sortable: true },
      { key: "name", label: "Name", sortable: true },
      { key: "registered_at", label: "Registered", sortable: true },
      { key: "role", label: "Role", sortable: false }
    ]
  },
  library: {
    icon: BookOpen,
    iconColor: "text-purple-400",
    iconBg: "bg-purple-900/50",
    columns: [
      { key: "email", label: "Email", sortable: true },
      { key: "name", label: "Name", sortable: true },
      { key: "books_added", label: "Books Added", sortable: true, align: "right" },
      { key: "titles", label: "Titles", sortable: false },
      { key: "first_add_date", label: "First Add", sortable: true },
      { key: "has_listened", label: "Listened?", sortable: false }
    ]
  },
  player: {
    icon: Play,
    iconColor: "text-amber-400",
    iconBg: "bg-amber-900/50",
    columns: [
      { key: "email", label: "Email", sortable: true },
      { key: "name", label: "Name", sortable: true },
      { key: "sessions", label: "Sessions", sortable: true, align: "right" },
      { key: "total_listen_seconds", label: "Listen Time", sortable: true, align: "right" },
      { key: "titles_played", label: "Titles", sortable: false },
      { key: "completed_any", label: "Completed?", sortable: false }
    ]
  },
  completed: {
    icon: CheckCircle,
    iconColor: "text-emerald-400",
    iconBg: "bg-emerald-900/50",
    columns: [
      { key: "email", label: "Email", sortable: true },
      { key: "name", label: "Name", sortable: true },
      { key: "completed_sessions", label: "Completed", sortable: true, align: "right" },
      { key: "titles_completed", label: "Titles", sortable: false },
      { key: "avg_listen_seconds", label: "Avg. Length", sortable: true, align: "right" },
      { key: "registered_at", label: "Registered", sortable: true }
    ]
  },
  never_listened: {
    icon: AlertTriangle,
    iconColor: "text-amber-400",
    iconBg: "bg-amber-900/50",
    columns: [
      { key: "email", label: "Email", sortable: true },
      { key: "name", label: "Name", sortable: true },
      { key: "registered_at", label: "Registered", sortable: true },
      { key: "days_since_registration", label: "Days Since", sortable: true, align: "right" }
    ]
  }
};

const ConversionDrillDownModal = ({ open, onOpenChange, title, stage, dateRange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [hasMore, setHasMore] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [hideInternal, setHideInternal] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [visitorDetailOpen, setVisitorDetailOpen] = useState(false);
  const [visitorDetail, setVisitorDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [summaryStats, setSummaryStats] = useState(null);

  const config = stageConfig[stage] || stageConfig.visitors;
  const Icon = config.icon;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchData = useCallback(async () => {
    if (!stage || !dateRange) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        stage: stage,
        start_date: dateRange.start,
        end_date: dateRange.end,
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_order: sortOrder
      });
      
      if (sortBy) {
        params.append('sort_by', sortBy);
      }
      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }
      if (hideInternal && stage === 'visitors') {
        params.append('hide_internal', 'true');
      }
      
      const response = await fetch(
        `${API_URL}/api/admin/analytics/conversion/list?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch data');
      }
      
      const data = await response.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
      setHasMore(data.has_more || false);
      
      // Extract summary stats from first item if available
      if (data.items?.[0]?._summary) {
        setSummaryStats(data.items[0]._summary);
      }
    } catch (err) {
      console.error('Error fetching conversion list:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [stage, dateRange, page, pageSize, sortBy, sortOrder, debouncedSearch, hideInternal]);

  useEffect(() => {
    if (open && stage) {
      fetchData();
    }
  }, [open, fetchData, stage]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setPage(1);
      setSearch('');
      setDebouncedSearch('');
      setSortBy(null);
      setSortOrder('desc');
      setItems([]);
      setSummaryStats(null);
      setSelectedVisitor(null);
      setVisitorDetail(null);
    }
  }, [open]);

  // Fetch visitor detail when selected
  const fetchVisitorDetail = async (anonymousId) => {
    setLoadingDetail(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/engagement/visitor/${anonymousId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch visitor details');
      }
      
      const data = await response.json();
      setVisitorDetail(data);
      setVisitorDetailOpen(true);
    } catch (err) {
      console.error('Error fetching visitor detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleRowClick = (item) => {
    if (config.clickable && stage === 'visitors') {
      setSelectedVisitor(item.anonymous_id);
      fetchVisitorDetail(item.anonymous_id);
    }
  };

  const handleSort = (field) => {
    if (!stageConfig[stage]?.columns.find(c => c.key === field)?.sortable) return;
    
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const getSortIcon = (field) => {
    const column = config.columns.find(c => c.key === field);
    if (!column?.sortable) return null;
    
    if (sortBy !== field) {
      return <ArrowUpDown className="w-4 h-4 text-slate-500" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-blue-400" />
      : <ArrowDown className="w-4 h-4 text-blue-400" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return (
      <div className="flex flex-col">
        <span>{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        <span className="text-xs text-slate-500">{date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
      </div>
    );
  };

  const formatListenTime = (seconds) => {
    if (!seconds || seconds === 0) return '—';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatCellValue = (item, column) => {
    const value = item[column.key];
    
    switch (column.key) {
      case 'first_visit':
      case 'last_visit':
        // Use formatDateTime for visitors stage to show time
        if (stage === 'visitors') {
          return formatDateTime(value);
        }
        return formatDate(value);
      
      case 'first_add_date':
      case 'registered_at':
        return formatDate(value);
      
      case 'total_listen_seconds':
      case 'avg_listen_seconds':
        return formatListenTime(value);
      
      case 'bounced':
        return value ? (
          <span className="text-amber-400 font-medium">Yes</span>
        ) : (
          <span className="text-slate-500">No</span>
        );
      
      case 'converted':
      case 'has_listened':
      case 'completed_any':
        return value ? (
          <span className="text-emerald-400 font-medium">Yes</span>
        ) : (
          <span className="text-slate-500">No</span>
        );
      
      case 'location':
        return value || 'Unknown';
      
      case 'titles':
      case 'titles_played':
      case 'titles_completed':
        if (Array.isArray(value) && value.length > 0) {
          const display = value.slice(0, 2).join(', ');
          const remaining = value.length - 2;
          return (
            <span title={value.join('\n')}>
              {display}
              {remaining > 0 && <span className="text-slate-500"> +{remaining}</span>}
            </span>
          );
        }
        return '—';
      
      case 'anonymous_id':
        return (
          <span className="font-mono text-xs text-slate-400">
            {value?.substring(0, 12)}...
          </span>
        );
      
      case 'role':
        return (
          <span className={`
            px-2 py-1 rounded text-xs font-medium
            ${value === 'admin' ? 'bg-amber-900/50 text-amber-300' : ''}
            ${value === 'publisher' ? 'bg-purple-900/50 text-purple-300' : ''}
            ${value === 'author' ? 'bg-blue-900/50 text-blue-300' : ''}
            ${value === 'reader' ? 'bg-slate-700 text-slate-300' : ''}
          `}>
            {value || 'reader'}
          </span>
        );
      
      case 'days_since_registration':
        return (
          <span className={value > 7 ? 'text-amber-400 font-medium' : 'text-slate-300'}>
            {value} days
          </span>
        );
      
      default:
        return value ?? '—';
    }
  };

  const totalPages = Math.ceil(total / pageSize);
  const showSearch = ['accounts', 'library', 'player', 'completed', 'never_listened'].includes(stage);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-5xl max-h-[90vh] bg-slate-900 border-slate-700 overflow-hidden flex flex-col"
        allowClickOutside={true}
        hideCloseButton={true}
      >
        {/* Close Button */}
        <button
          onClick={() => onOpenChange(false)}
          className="absolute right-4 top-4 p-1 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition-colors z-10"
          data-testid="close-conversion-modal-btn"
        >
          <X className="w-5 h-5" />
          <span className="sr-only">Close</span>
        </button>

        <DialogHeader className="shrink-0 pr-10">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${config.iconBg}`}>
              <Icon className={`w-5 h-5 ${config.iconColor}`} />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold text-white">
                {title}
              </DialogTitle>
              <p className="text-sm text-slate-400 mt-1">
                {total.toLocaleString()} {stage === 'visitors' ? 'visitors' : 'users'} • {dateRange?.start} to {dateRange?.end}
              </p>
            </div>
          </div>
          
          {/* Summary Stats for Visitors */}
          {stage === 'visitors' && summaryStats && (
            <div className="flex items-center gap-6 mt-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-slate-400">Likely Unique:</span>
                <span className="font-semibold text-cyan-400">{summaryStats.unique_by_fingerprint?.toLocaleString() || total}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-400">Bounce Rate:</span>
                <span className="font-semibold text-amber-400">
                  {items.length > 0 
                    ? Math.round((items.filter(i => i.bounced).length / items.length) * 100)
                    : 0}%
                </span>
              </div>
              <label className="flex items-center gap-2 cursor-pointer ml-auto">
                <input
                  type="checkbox"
                  checked={hideInternal}
                  onChange={(e) => {
                    setHideInternal(e.target.checked);
                    setPage(1);
                  }}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-purple-500 focus:ring-purple-500"
                />
                <span className="text-slate-400">Hide internal traffic</span>
              </label>
            </div>
          )}
        </DialogHeader>

        {/* Search Bar */}
        {showSearch && (
          <div className="relative shrink-0 my-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              data-testid="conversion-search-input"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        {/* Table Container */}
        <div className="flex-1 overflow-auto min-h-0">
          {loading && items.length === 0 ? (
            <div className="flex justify-center items-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-red-400">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                className="mt-4 border-red-700 text-red-400 hover:bg-red-900/30"
              >
                Retry
              </Button>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16">
              <Icon className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No data found</p>
            </div>
          ) : (
            <Table>
              <TableHeader className="sticky top-0 bg-slate-900">
                <TableRow className="border-slate-700 hover:bg-transparent">
                  {config.columns.map((column) => (
                    <TableHead 
                      key={column.key}
                      className={`
                        text-slate-300 font-bold 
                        ${column.sortable ? 'cursor-pointer hover:text-white transition-colors' : ''}
                        ${column.align === 'right' ? 'text-right' : ''}
                      `}
                      onClick={() => column.sortable && handleSort(column.key)}
                    >
                      <div className={`flex items-center gap-2 ${column.align === 'right' ? 'justify-end' : ''}`}>
                        {column.label}
                        {getSortIcon(column.key)}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow 
                    key={item.user_id || item.anonymous_id || index} 
                    className={`border-slate-800 hover:bg-slate-800/50 ${config.clickable ? 'cursor-pointer' : ''}`}
                    onClick={() => handleRowClick(item)}
                  >
                    {config.columns.map((column) => (
                      <TableCell 
                        key={column.key}
                        className={`text-slate-300 ${column.align === 'right' ? 'text-right' : ''}`}
                      >
                        {formatCellValue(item, column)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Pagination */}
        {total > pageSize && (
          <div className="shrink-0 flex items-center justify-between pt-4 border-t border-slate-800">
            <p className="text-sm text-slate-400">
              Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
                className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                data-testid="conv-prev-page-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-400 px-2">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={!hasMore || loading}
                className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                data-testid="conv-next-page-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Loading overlay for pagination */}
        {loading && items.length > 0 && (
          <div className="absolute inset-0 bg-slate-900/50 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
          </div>
        )}
      </DialogContent>

      {/* Visitor Detail Modal */}
      <Dialog open={visitorDetailOpen} onOpenChange={setVisitorDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] bg-slate-900 border-slate-700 overflow-hidden flex flex-col">
          <button
            onClick={() => setVisitorDetailOpen(false)}
            className="absolute right-4 top-4 p-1 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>
          
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-slate-400" />
              Visitor Details
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
            </div>
          ) : visitorDetail ? (
            <div className="flex-1 overflow-y-auto space-y-6 pr-2">
              {/* Overview Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Visitor ID</p>
                  <p className="font-mono text-sm text-slate-300">{visitorDetail.anonymous_id}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Location</p>
                  <p className="text-slate-300">
                    {visitorDetail.city !== 'Unknown' && `${visitorDetail.city}, `}
                    {visitorDetail.region !== 'Unknown' && `${visitorDetail.region}, `}
                    {visitorDetail.country}
                  </p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">First Visit</p>
                  <p className="text-slate-300">{visitorDetail.first_visit ? new Date(visitorDetail.first_visit).toLocaleString() : '—'}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Last Visit</p>
                  <p className="text-slate-300">{visitorDetail.last_visit ? new Date(visitorDetail.last_visit).toLocaleString() : '—'}</p>
                </div>
              </div>
              
              {/* Stats Row */}
              <div className="flex items-center gap-6 px-2">
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">Pages Viewed:</span>
                  <span className="font-semibold text-white">{visitorDetail.total_pages_viewed}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">Sessions:</span>
                  <span className="font-semibold text-white">{visitorDetail.total_sessions}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">Bounced:</span>
                  <span className={`font-semibold ${visitorDetail.bounced ? 'text-amber-400' : 'text-emerald-400'}`}>
                    {visitorDetail.bounced ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">Converted:</span>
                  <span className={`font-semibold ${visitorDetail.converted ? 'text-emerald-400' : 'text-slate-500'}`}>
                    {visitorDetail.converted ? 'Yes' : 'No'}
                  </span>
                </div>
                {visitorDetail.is_internal_traffic && (
                  <span className="px-2 py-1 bg-amber-900/50 text-amber-300 text-xs font-medium rounded">
                    Internal
                  </span>
                )}
              </div>
              
              {/* Page Journey */}
              <div>
                <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Page Journey ({visitorDetail.page_journey?.length || 0} pages)
                </h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {visitorDetail.page_journey?.map((page, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm">
                      <span className="text-slate-500 font-mono text-xs w-6">{idx + 1}.</span>
                      <span className="text-slate-300">{page.friendly_name}</span>
                      <span className="text-slate-600 text-xs">{page.route}</span>
                      <span className="text-slate-500 text-xs ml-auto">
                        {new Date(page.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                  ))}
                  {(!visitorDetail.page_journey || visitorDetail.page_journey.length === 0) && (
                    <p className="text-slate-500 text-sm">No page views recorded</p>
                  )}
                </div>
              </div>
              
              {/* Sample Activity */}
              {visitorDetail.sample_activity?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                    <Play className="w-4 h-4" />
                    Sample Plays ({visitorDetail.sample_activity.length})
                  </h4>
                  <div className="space-y-2">
                    {visitorDetail.sample_activity.map((sample, idx) => (
                      <div key={idx} className="bg-slate-800/30 rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="text-slate-200 font-medium">{sample.title}</p>
                          <p className="text-xs text-slate-500">
                            {sample.timestamp ? new Date(sample.timestamp).toLocaleString() : ''}
                          </p>
                        </div>
                        {sample.listen_duration > 0 && (
                          <span className="text-sm text-slate-400">
                            {Math.floor(sample.listen_duration / 60)}m listened
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              No visitor data found
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default ConversionDrillDownModal;
